import { CalendarClock, Download, Plus, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { CATEGORY_OPTIONS } from "./category-badge";

interface FilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  dateFrom: string;
  onDateFromChange: (value: string) => void;
  dateTo: string;
  onDateToChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  valuer: string;
  onValuerChange: (value: string) => void;
  category: string;
  onCategoryChange: (value: string) => void;
  includeSuperseded: boolean;
  onIncludeSupersededChange: (value: boolean) => void;
  onApply: () => void;
  onExport: () => void;
  onExpiringFilter: () => void;
  valuers: string[];
  isExpiringActive: boolean;
}

export function FilterBar({
  search,
  onSearchChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  status,
  onStatusChange,
  valuer,
  onValuerChange,
  category,
  onCategoryChange,
  includeSuperseded,
  onIncludeSupersededChange,
  onApply,
  onExport,
  onExpiringFilter,
  valuers,
  isExpiringActive,
}: FilterBarProps): React.ReactElement {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        {/* Search */}
        <div className="flex-1 min-w-48">
          <label htmlFor="filter-search" className="text-xs font-medium text-muted-foreground mb-1 block">
            Search
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id="filter-search"
              type="text"
              placeholder="Address, name, or ID..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onApply()}
              className="flex h-9 w-full rounded-md border border-input bg-white pl-10 pr-3 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="filter-search"
            />
          </div>
        </div>

        {/* Date From */}
        <div className="w-36">
          <label htmlFor="filter-date-from" className="text-xs font-medium text-muted-foreground mb-1 block">
            Date From
          </label>
          <input
            id="filter-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        {/* Date To */}
        <div className="w-36">
          <label htmlFor="filter-date-to" className="text-xs font-medium text-muted-foreground mb-1 block">
            Date To
          </label>
          <input
            id="filter-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        {/* Status */}
        <div className="w-36">
          <label htmlFor="filter-status" className="text-xs font-medium text-muted-foreground mb-1 block">
            Status
          </label>
          <select
            id="filter-status"
            value={status}
            onChange={(e) => onStatusChange(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="filter-status"
          >
            <option value="">Any Status</option>
            <option value="active">Active</option>
            <option value="cancelled">Cancelled</option>
            <option value="superseded">Superseded</option>
          </select>
        </div>

        {/* Valuer */}
        <div className="w-40">
          <label htmlFor="filter-valuer" className="text-xs font-medium text-muted-foreground mb-1 block">
            Valuer
          </label>
          <select
            id="filter-valuer"
            value={valuer}
            onChange={(e) => onValuerChange(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="filter-valuer"
          >
            <option value="">Any Valuer</option>
            {valuers.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>

        {/* Category */}
        <div className="w-44">
          <label htmlFor="filter-category" className="text-xs font-medium text-muted-foreground mb-1 block">
            Category
          </label>
          <select
            id="filter-category"
            value={category}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="filter-category"
          >
            <option value="">Any Category</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Second row: quick filters, checkbox, actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant={isExpiringActive ? "default" : "outline"}
            onClick={onExpiringFilter}
            data-testid="filter-expiring"
          >
            <CalendarClock className="h-3.5 w-3.5" />
            Expiring soon
          </Button>
          <label htmlFor="filter-superseded" className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              id="filter-superseded"
              type="checkbox"
              checked={includeSuperseded}
              onChange={(e) => onIncludeSupersededChange(e.target.checked)}
              className="h-4 w-4 rounded border-input"
              data-testid="filter-superseded"
            />
            Include superseded
          </label>
          <Button size="sm" variant="outline" onClick={onApply} data-testid="filter-go">
            Go
          </Button>
          <Button size="sm" variant="outline" onClick={onExport} data-testid="filter-export">
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </div>
        <Link to="/insurance/new">
          <Button size="sm" data-testid="create-record-button">
            <Plus className="h-3.5 w-3.5" />
            New Record
          </Button>
        </Link>
      </div>
    </div>
  );
}
